import { useDocumentListLazyQuery } from '@client/graphql/__generated__/document-operations';
import {
  DocumentFragment,
  DocumentListArgs,
} from '@client/graphql/__generated__/types';
import { debounce } from 'lodash';
import { FC, useCallback, useMemo } from 'react';
import { AsyncSelect, AsyncSelectProps } from './Select';

export interface DocumentSelectOption {
  value?: Partial<Omit<DocumentFragment, '__typename'>>;
}

export const DocumentSelect: FC<
  (
    | AsyncSelectProps<DocumentSelectOption>
    | AsyncSelectProps<DocumentSelectOption, true>
  ) & { filters?: DocumentListArgs }
> = (props) => {
  const [fetchDocuments, { loading }] = useDocumentListLazyQuery();

  const loadOptions = useCallback(
    (
      inputValue: string,
      callback: (options: DocumentSelectOption[]) => void
    ) => {
      void fetchDocuments({
        initialFetchPolicy: 'cache-first',
        fetchPolicy: 'cache-first',
        variables: {
          filters: {
            ...props.filters,
            search: inputValue || undefined,
          },
        },
      }).then(({ data }) => {
        if (data?.documentList?.results) {
          callback(
            data.documentList.results.map((doc) => ({
              value: doc,
            })) || []
          );
        } else {
          callback([]);
        }
      });
    },
    [fetchDocuments, props.filters]
  );

  const formatOptionLabel = useCallback((option: DocumentSelectOption) => {
    if (!option.value) {
      return 'Invalid Document Option';
    }

    const { name, form } = option.value;

    return `${name} (${form})`;
  }, []);

  const loadOptionsDebounced = useMemo(
    () => debounce(loadOptions, 500),
    [loadOptions]
  );

  return (
    <AsyncSelect
      isClearable
      formatOptionLabel={formatOptionLabel}
      isLoading={loading}
      loadOptions={loadOptionsDebounced}
      placeholder="Select Document"
      size="lg"
      {...props}
    />
  );
};
